import { isAxiosError } from 'axios';
import { ref, type Ref, type UnwrapRef } from 'vue';
import { deepClone } from './clone';

export type FormErrors<T> = {
  [K in keyof T]: string[];
} & {
  [key: string]: string[];
};

interface FormMethods<T> {
  form: Ref<UnwrapRef<T>>;
  errors: Ref<Map<string, Partial<FormErrors<any>>> & Omit<Map<string, Partial<FormErrors<any>>>, keyof Map<any, any>>>;
  setErrors: (value: unknown) => void;
  clearErrors: () => void;
  clearForm: () => void;
  reset: () => void;
}

export function errorHandler(error: any) {
  const { message } = error.response?.data ?? error;

  if (isAxiosError(error)) {
    const errors = error.response?.data.errors;

    if (errors) {
      return { errors };
    } else {
      console.warn(message);
      return { errors };
    }
  } else if (error instanceof Error) {
    console.warn(message);
    return { errors: error };
  } else {
    throw error;
  }
}

export function useForm<T extends Record<string, unknown>>(values: T): FormMethods<T> {
  const initialValues = ref(deepClone(values));

  const form = ref<T>(values);

  const errors = ref(new Map<string, Partial<FormErrors<any>>>());

  function setErrors(values: any) {
    errors.value = new Map<string, Partial<FormErrors<any>>>(Object.entries(values));
  }

  function clearForm() {
    form.value = deepClone(initialValues.value);
  }

  function clearErrors() {
    errors.value.clear();
  }

  function reset() {
    clearForm();
    clearErrors();
  }

  return {
    form,
    errors,
    setErrors,
    clearErrors,
    clearForm,
    reset,
  };
}
